// THE CONDITION AND COMPS STEPS' SHARED CHROME (WEB-PARITY-3): the app's words stamped with their id, the step hero,
// the app's BrandCard, and its sheet — a modal form over the page, as `AppAlert` is a modal alert. Chrome only: nothing
// here decides anything.

import { useEffect, useId, useRef, type ReactNode } from "react";
import { say, sayWith, type Parity3WordingId } from "./parity3Wording";

/** One of the app's sentences, stamped with its id (`data-held-copy`), as every word this client holds is. */
export function Said({
  id,
  as: Tag = "span",
  className,
  values,
}: {
  id: Parity3WordingId;
  as?: "h1" | "h2" | "h3" | "p" | "span" | "b" | "small";
  className?: string;
  values?: Readonly<Record<string, string | number>>;
}) {
  return (
    <Tag className={className} data-held-copy={id}>
      {values === undefined ? say(id) : sayWith(id, values)}
    </Tag>
  );
}

export type HeroIcon = "wrench" | "house" | "search";

/** The app's `ScreenHero`: a tinted disc, the title, the body (where the screen has one). */
export function StepHero({ icon, titleId, bodyId }: { icon: HeroIcon; titleId: Parity3WordingId; bodyId?: Parity3WordingId }) {
  const paths: Record<HeroIcon, string> = {
    // The app's `wrench.and.screwdriver` (COND:24) and `house.and.flag.fill` (CV:191), drawn plainly.
    wrench: "M22.7 19 13.6 9.9c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4Z",
    house: "M12 3.2 2.8 11a1 1 0 0 0 1.3 1.5l.9-.8V20a1 1 0 0 0 1 1h4.2v-5.4h3.6V21H18a1 1 0 0 0 1-1v-8.3l.9.8a1 1 0 0 0 1.3-1.5Z",
    // The app's `magnifyingglass` (the search card's status bar, CV:311), as `parity4Parts`' `search` draws it.
    search: "M10 3a7 7 0 0 1 5.6 11.2l5.1 5.1-1.4 1.4-5.1-5.1A7 7 0 1 1 10 3zm0 2a5 5 0 1 0 0 10 5 5 0 0 0 0-10z",
  };
  return (
    <header className="step-hero">
      <span className="hero-disc" aria-hidden="true">
        <svg width="24" height="24" viewBox="0 0 24 24" focusable="false">
          <path d={paths[icon]} fill="currentColor" />
        </svg>
      </span>
      <Said id={titleId} as="h1" />
      {bodyId !== undefined && <Said id={bodyId} as="p" className="sub" />}
    </header>
  );
}

/** The app's `BrandCard`: an uppercase title, the body, a muted footer. */
export function Card({
  titleId,
  footer,
  accent = false,
  children,
  ...marks
}: {
  titleId: Parity3WordingId;
  footer?: ReactNode;
  accent?: boolean;
  children: ReactNode;
} & Record<`data-${string}`, string | number | undefined>) {
  return (
    <section className={accent ? "brand-card accent" : "brand-card"} {...marks}>
      <h3 className="brand-card-title">
        <Said id={titleId} />
      </h3>
      <div className="brand-card-body">{children}</div>
      {footer !== undefined && <div className="card-footer">{footer}</div>}
    </section>
  );
}

/**
 * The app's `.sheet`: a title bar with its two actions (the app's toolbar — a cancel on the left where it has one, the
 * confirming action on the right), and a form under it. A modal `dialog`: focus moves into it and returns when it
 * closes, and Escape is the cancel where there is one.
 */
export function Sheet({
  titleId,
  kind,
  cancel,
  confirm,
  children,
}: {
  titleId: Parity3WordingId;
  kind: string;
  cancel?: { id: Parity3WordingId; onPress: () => void };
  confirm: { id: Parity3WordingId; onPress: () => void; disabled?: boolean };
  children: ReactNode;
}) {
  const headingId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    return () => {
      before?.focus?.();
    };
  }, []);
  return (
    <div className="alert-backdrop">
      <div
        ref={dialog}
        tabIndex={-1}
        className="app-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-sheet={kind}
        onKeyDown={(e) => {
          if (e.key === "Escape" && cancel !== undefined) {
            e.preventDefault();
            cancel.onPress();
          }
        }}
      >
        <div className="app-sheet-bar">
          {cancel === undefined ? <span /> : (
            <button type="button" className="btn quiet" data-held-copy={cancel.id} onClick={cancel.onPress}>{say(cancel.id)}</button>
          )}
          <h2 id={headingId} data-held-copy={titleId}>{say(titleId)}</h2>
          <button
            type="button"
            className="btn quiet strong"
            data-held-copy={confirm.id}
            disabled={confirm.disabled === true}
            onClick={confirm.onPress}
          >
            {say(confirm.id)}
          </button>
        </div>
        <div className="app-sheet-body">{children}</div>
      </div>
    </div>
  );
}
